import { Archive, BarChart3, Download, Plus, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { endOfDayParam } from '@/components/ui/date-range-filter';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ActiveFilterChips,
  DateRangeButton,
  FilterSelect,
  FiltersPopover,
  ListSearch,
  ToolbarSelect,
  TriStateFilter,
  resolveDateWindow,
  type ActiveFilterChip,
} from '@/components/ui/list-filters';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { exportCsv, type ApiError } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { PurgeArchivedButton } from '@/features/crm/components/PurgeArchivedButton';
import { BASE as BIDS_BASE, type BidListParams } from '../api';
import { OwnerFilter } from '../components/OwnerFilter';
import {
  BID_CHANNELS,
  BID_HIRE_RATE_MINS,
  BID_ORIGINS,
  BID_RATE_BANDS,
  BID_STATUSES,
  BID_VS_MEDIAN,
  bidStatusLabel,
  bidStatusVariant,
  channelLabel,
  hireRateMinLabel,
  originLabel,
  rateBandLabel,
  vsMedianLabel,
} from '../constants';
import { bidTitle, formatBidValue, formatRate } from '../format';
import { useArchiveBid, useBids, usePurgeBid, useRestoreBid } from '../queries';
import { BIDS_FILTER_DEFAULTS, useBidsUi } from '../store/viewPrefs';
import type { Bid } from '../types';

/** Columns the backend lets us order by. */
type BidOrderingColumn =
  | 'submitted_at'
  | 'status'
  | 'bid_rate'
  | 'bid_range_median'
  | 'client_hire_rate_pct'
  | 'client_country'
  | 'connects_spent';

/** Filters that live in the "Filters" popover (the toolbar keeps search,
 *  status, owner and the submitted window inline). */
const POPOVER_FILTERS = [
  'channel',
  'origin',
  'vsMedian',
  'rateBand',
  'boosted',
  'converted',
  'clientCountry',
  'clientHireRateMin',
] as const;
type PopoverFilter = (typeof POPOVER_FILTERS)[number];

export function BidsListPage() {
  const navigate = useNavigate();
  const ui = useBidsUi();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);
  const [page, setPage] = useState(1);

  // "Last N days" is held as a count and re-anchored on today here, so it keeps
  // rolling across reloads; a fixed range passes through unchanged.
  const submittedWindow = {
    from: ui.submittedFrom,
    to: ui.submittedTo,
    rollingDays: ui.submittedRolling,
  };
  const submitted = resolveDateWindow(submittedWindow);

  // Whitespace-only country text sends no filter, so it never counts as one.
  const differsFromDefault = (k: keyof typeof BIDS_FILTER_DEFAULTS) =>
    k === 'clientCountry' ? ui.clientCountry.trim() !== '' : ui[k] !== BIDS_FILTER_DEFAULTS[k];
  // The archived toggle counts too, so Reset (which turns it off) shows with it —
  // the same as Leads.
  const filtersActive =
    searchInput !== '' ||
    ui.showArchived ||
    (Object.keys(BIDS_FILTER_DEFAULTS) as (keyof typeof BIDS_FILTER_DEFAULTS)[]).some(differsFromDefault);

  function resetFilters() {
    setSearchInput('');
    setPage(1);
    ui.reset();
  }

  // One param builder shared by the list and the CSV export so both read the
  // same filtered set. (The roll-up has its own, URL-held filters.)
  const params: BidListParams = {
    search: search || undefined,
    channel: ui.channel === 'all' ? undefined : ui.channel,
    status: ui.status === 'all' ? undefined : ui.status,
    origin: ui.origin === 'all' ? undefined : ui.origin,
    owner: ui.owner ? Number(ui.owner) : undefined,
    mine: ui.mine ? true : undefined,
    bid_vs_median: ui.vsMedian === 'all' ? undefined : ui.vsMedian,
    rate_band: ui.rateBand === 'all' ? undefined : ui.rateBand,
    boosted: ui.boosted === 'all' ? undefined : ui.boosted === 'yes',
    client_country: ui.clientCountry.trim() || undefined,
    client_hire_rate_min: ui.clientHireRateMin ? Number(ui.clientHireRateMin) : undefined,
    converted: ui.converted === 'all' ? undefined : ui.converted === 'yes',
    submitted_after: submitted.from || undefined,
    submitted_before: endOfDayParam(submitted.to),
    archived: ui.showArchived ? true : undefined,
    ordering: ui.ordering,
  };

  const { data, isPending, isError, error, isFetching } = useBids({ ...params, page });
  const archive = useArchiveBid();
  const restore = useRestoreBid();
  const purge = usePurgeBid();
  const results = data?.results ?? [];

  // Every filter change restarts paging.
  const pick = (setter: (v: string) => void) => (v: string) => {
    setPage(1);
    setter(v);
  };

  const setters: Record<PopoverFilter, (v: string) => void> = {
    channel: ui.setChannel,
    origin: ui.setOrigin,
    vsMedian: ui.setVsMedian,
    rateBand: ui.setRateBand,
    boosted: ui.setBoosted,
    converted: ui.setConverted,
    clientCountry: ui.setClientCountry,
    clientHireRateMin: ui.setClientHireRateMin,
  };
  const clearPopoverFilter = (k: PopoverFilter) => pick(setters[k])(BIDS_FILTER_DEFAULTS[k]);
  const activePopover = POPOVER_FILTERS.filter(differsFromDefault);

  const chipLabel: Record<PopoverFilter, string> = {
    channel: `Channel: ${channelLabel(ui.channel)}`,
    origin: `Source: ${originLabel(ui.origin)}`,
    vsMedian: vsMedianLabel(ui.vsMedian),
    rateBand: `Rate: ${rateBandLabel(ui.rateBand)}`,
    boosted: ui.boosted === 'yes' ? 'Boosted' : 'Not boosted',
    converted: ui.converted === 'yes' ? 'Has a lead' : 'No lead yet',
    clientCountry: `Country: ${ui.clientCountry.trim()}`,
    clientHireRateMin: `Client hire rate: ${hireRateMinLabel(ui.clientHireRateMin)}`,
  };
  const chips: ActiveFilterChip[] = activePopover.map((k) => ({
    key: k,
    label: chipLabel[k],
    onRemove: () => clearPopoverFilter(k),
  }));

  // A persisted threshold from before the presets existed still shows as itself.
  const isLegacyHireRate =
    ui.clientHireRateMin !== '' && !BID_HIRE_RATE_MINS.some((o) => o.value === ui.clientHireRateMin);
  const hireRateOptions = isLegacyHireRate
    ? [...BID_HIRE_RATE_MINS, { value: ui.clientHireRateMin, label: hireRateMinLabel(ui.clientHireRateMin) }]
    : BID_HIRE_RATE_MINS;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Bids</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/crm/bids/rollup')}>
            <BarChart3 /> Weekly roll-up
          </Button>
          <Button onClick={() => navigate('/crm/bids/new')}>
            <Plus /> New bid
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <ListSearch
              value={searchInput}
              onChange={(v) => {
                setPage(1);
                setSearchInput(v);
              }}
              placeholder="Search bids…"
              className="w-full sm:w-56"
            />
            <ToolbarSelect
              value={ui.status}
              onChange={pick(ui.setStatus)}
              allLabel="All statuses"
              options={BID_STATUSES}
              ariaLabel="Status"
            />
            <OwnerFilter
              mine={ui.mine}
              owner={ui.owner}
              onChange={({ mine, owner }) => {
                setPage(1);
                ui.setMine(mine);
                ui.setOwner(owner);
              }}
            />
            <DateRangeButton
              label="Submitted"
              idPrefix="bid-submitted"
              window={submittedWindow}
              onChange={(w) => {
                setPage(1);
                ui.setSubmittedWindow(w);
              }}
            />
            <FiltersPopover
              activeFilters={activePopover.length}
              onClear={() => activePopover.forEach(clearPopoverFilter)}
              contentClassName="w-80"
            >
              <div className="grid grid-cols-2 gap-3">
                <FilterSelect
                  label="Channel"
                  value={ui.channel}
                  onChange={pick(ui.setChannel)}
                  allLabel="All channels"
                  options={BID_CHANNELS}
                />
                <FilterSelect
                  label="Source"
                  value={ui.origin}
                  onChange={pick(ui.setOrigin)}
                  allLabel="Any"
                  options={BID_ORIGINS}
                />
                <FilterSelect
                  label="Price vs job median"
                  value={ui.vsMedian}
                  onChange={pick(ui.setVsMedian)}
                  allLabel="Any"
                  options={BID_VS_MEDIAN}
                />
                <FilterSelect
                  label="Rate band"
                  value={ui.rateBand}
                  onChange={pick(ui.setRateBand)}
                  allLabel="Any rate"
                  options={BID_RATE_BANDS}
                />
              </div>
              <TriStateFilter
                label="Boosted"
                value={ui.boosted}
                onChange={pick(ui.setBoosted)}
              />
              <TriStateFilter
                label="Converted to a lead"
                value={ui.converted}
                onChange={pick(ui.setConverted)}
              />
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="bid-client-country" className="text-xs">
                    Client country
                  </Label>
                  <Input
                    id="bid-client-country"
                    value={ui.clientCountry}
                    onChange={(e) => pick(ui.setClientCountry)(e.target.value)}
                    placeholder="e.g. Germany"
                    className="h-8"
                  />
                </div>
                <FilterSelect
                  label="Client hire rate"
                  value={ui.clientHireRateMin || 'all'}
                  onChange={(v) => pick(ui.setClientHireRateMin)(v === 'all' ? '' : v)}
                  allLabel="Any"
                  options={hireRateOptions}
                />
              </div>
            </FiltersPopover>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="icon"
              variant={ui.showArchived ? 'default' : 'outline'}
              onClick={() => {
                setPage(1);
                ui.setShowArchived(!ui.showArchived);
              }}
              aria-pressed={ui.showArchived}
              aria-label={ui.showArchived ? 'Showing archived bids' : 'Show archived bids'}
              title={ui.showArchived ? 'Showing archived bids' : 'Show archived bids'}
            >
              <Archive className="size-4" />
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={() => void exportCsv(BIDS_BASE, { ...params, export: 'csv' })}
              aria-label="Export bids as CSV"
              title="Export bids as CSV"
            >
              <Download className="size-4" />
            </Button>
          </div>
        </div>
        {filtersActive ? (
          <ActiveFilterChips chips={chips} onResetAll={resetFilters} />
        ) : null}
      </div>

      {isError ? (
        <p className="text-destructive text-sm">{(error as unknown as ApiError).message}</p>
      ) : isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : (
        <BidsTable
          bids={results}
          showArchived={ui.showArchived}
          onRowClick={(b) => navigate(`/crm/bids/${b.public_id}`)}
          onArchive={(b) => archive.mutate(b.id)}
          onRestore={(b) => restore.mutate(b.id)}
          onPurge={(b) => purge.mutate(b.id)}
          ordering={ui.ordering}
          onOrderingChange={ui.setOrdering}
        />
      )}

      {data && data.count > 0 ? (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground text-sm">
            {data.count} {data.count === 1 ? 'bid' : 'bids'}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!data.previous || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button
              variant="outline"
              size="sm"
              disabled={!data.next || isFetching}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

interface BidsTableProps {
  bids: Bid[];
  showArchived: boolean;
  onRowClick: (b: Bid) => void;
  onArchive: (b: Bid) => void;
  onRestore: (b: Bid) => void;
  onPurge: (b: Bid) => void;
  ordering: string | undefined;
  onOrderingChange: (next: string | undefined) => void;
}

function BidsTable({
  bids,
  showArchived,
  onRowClick,
  onArchive,
  onRestore,
  onPurge,
  ordering,
  onOrderingChange,
}: BidsTableProps) {
  const head = (column: BidOrderingColumn, label: string, className?: string) => (
    <SortableTableHead<BidOrderingColumn>
      column={column}
      value={ordering}
      onChange={onOrderingChange}
      className={className}
    >
      {label}
    </SortableTableHead>
  );
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Job</TableHead>
            {head('status', 'Status')}
            {head('submitted_at', 'Submitted', 'hidden md:table-cell')}
            {head('bid_rate', 'Our bid', 'hidden sm:table-cell')}
            {head('bid_range_median', 'Job median', 'hidden lg:table-cell')}
            {head('client_country', 'Client', 'hidden lg:table-cell')}
            {head('connects_spent', 'Connects', 'hidden xl:table-cell')}
            <TableHead className="hidden md:table-cell">Owner</TableHead>
            <TableHead className="w-16 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {bids.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} className="text-muted-foreground py-10 text-center">
                {showArchived ? 'No archived bids.' : 'No bids found.'}
              </TableCell>
            </TableRow>
          ) : (
            bids.map((b) => (
              <TableRow key={b.id} className="cursor-pointer" onClick={() => onRowClick(b)}>
                <TableCell className="max-w-72">
                  <div className="truncate font-medium">{bidTitle(b)}</div>
                  <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    {channelLabel(b.channel)}
                    {b.origin === 'manual' ? <Badge variant="outline">Manual</Badge> : null}
                    {b.boosted ? <Badge variant="secondary">Boosted</Badge> : null}
                    {b.lead_public_id ? <Badge variant="outline">Lead</Badge> : null}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={bidStatusVariant(b.status)}>{bidStatusLabel(b.status)}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {b.submitted_at ? new Date(b.submitted_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell className="hidden tabular-nums sm:table-cell">
                  {formatBidValue(b)}
                  {b.bid_vs_median ? (
                    <span className="text-muted-foreground ml-1 text-xs">
                      ({vsMedianLabel(b.bid_vs_median).toLowerCase()})
                    </span>
                  ) : null}
                </TableCell>
                <TableCell className="text-muted-foreground hidden tabular-nums lg:table-cell">
                  {formatRate(b.bid_range_median, b.currency, b.job_type)}
                </TableCell>
                <TableCell className="text-muted-foreground hidden lg:table-cell">
                  {b.client_country || '—'}
                  {b.client_hire_rate_pct ? ` · ${Number(b.client_hire_rate_pct)}% hire` : ''}
                </TableCell>
                <TableCell className="text-muted-foreground hidden tabular-nums xl:table-cell">
                  {b.connects_spent ?? '—'}
                </TableCell>
                <TableCell className="text-muted-foreground hidden md:table-cell">
                  {b.owner_name ?? '—'}
                </TableCell>
                <TableCell className="text-right">
                  {b.is_archived ? (
                    <span className="inline-flex items-center justify-end">
                      <Button
                        size="icon"
                        variant="ghost"
                        aria-label={`Restore ${bidTitle(b)}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestore(b);
                        }}
                      >
                        <RotateCcw className="size-4" />
                      </Button>
                      <PurgeArchivedButton name={bidTitle(b)} onPurge={() => onPurge(b)} />
                    </span>
                  ) : (
                    <Button
                      size="icon"
                      variant="ghost"
                      aria-label={`Archive ${bidTitle(b)}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onArchive(b);
                      }}
                    >
                      <Archive className="size-4" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
